import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  IconButton,
  Tooltip,
  Dialog,
  DialogContent,
  Typography,
  CircularProgress,
  Chip,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Divider,
  Grid
} from '@mui/material';
import LeaderboardIcon from '@mui/icons-material/Leaderboard';
import { io } from 'socket.io-client';
import { bindGameResync } from '../../../utils/bindGameResync';
import QuizQuestion from '../quiz/normalMode/QuizQuestion';
import IncomingCountdownOverlay from './IncomingCountdownOverlay';
import { getGuestDisplayName } from '../../../utils/guestDisplayUtils';

const BingoFlashIframe = ({ url }) => {
  const [loading, setLoading] = useState(true);
  const [session, setSession] = useState(null);
  const [showQuestion, setShowQuestion] = useState(false);
  const [quizData, setQuizData] = useState(null);
  const [questionVersion, setQuestionVersion] = useState(0);
  const [showLb, setShowLb] = useState(false);
  const [leaderboard, setLeaderboard] = useState([]);
  const deliveryTimerRef = useRef(null);
  const socketRef = useRef(null);
  const [pushedQuizlets, setPushedQuizlets] = useState([]);
  const [currentFlashPayload, setCurrentFlashPayload] = useState(null);
  const [remainingSecondsForCurrent, setRemainingSecondsForCurrent] = useState(null);
  const [currentPlayingId, setCurrentPlayingId] = useState(null);
  const [incomingCountdown, setIncomingCountdown] = useState(null);
  const incomingCountdownIntervalRef = useRef(null);
  const [participant, setParticipant] = useState(null);
  const [quizletStats, setQuizletStats] = useState({});
  const quizletStatsRef = useRef({});
  const activeQuizletIdRef = useRef(null);

  // Safely derive time taken for a quizlet – prefer backend `time_taken`,
  // otherwise fall back to difference between quizlet start (encoded in quizlet_id)
  // and `completed_at` if both are available.
  const getQuizletTimeTakenSeconds = useCallback((stat) => {
    if (!stat) return null;
    if (stat.time_taken !== undefined && stat.time_taken !== null) {
      // Backend already calculated this; just return it (ensure it's a number)
      const n = Number(stat.time_taken);
      return Number.isFinite(n) ? n : null;
    }

    // Derive from quizlet_id + completed_at if possible.
    try {
      const quizletId = String(stat.quizlet_id || '');
      const parts = quizletId.split('_');
      if (parts.length < 2 || !stat.completed_at) return null;

      const pushedIso = parts.slice(1).join('_');
      const startMs = new Date(pushedIso).getTime();
      const endMs = new Date(stat.completed_at).getTime();
      if (!Number.isFinite(startMs) || !Number.isFinite(endMs) || endMs <= startMs) return null;

      const diffSec = Math.round((endMs - startMs) / 1000);
      return diffSec >= 0 ? diffSec : null;
    } catch {
      return null;
    }
  }, []);

  const baseUrl = useMemo(() => process.env.REACT_APP_API_URL || 'http://localhost:3001', []);

  const getDeviceId = () => {
    let deviceId = localStorage.getItem('device_id');
    if (!deviceId) {
      deviceId = 'device_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
      localStorage.setItem('device_id', deviceId);
    }
    return deviceId;
  };

  const fetchActiveFlashSession = useCallback(async () => {
    const res = await fetch(`${baseUrl}/api/live-quiz/flash/active`);
    if (!res.ok) throw new Error('Failed to get active flash sessions');
    const data = await res.json();
    if (!data?.sessions?.length) throw new Error('No active flash session');
    return data.sessions[0];
  }, [baseUrl]);

  const fetchQuizletStats = useCallback(async (sessionId, participantId) => {
    if (!sessionId || !participantId) return;
    try {
      // console.log('BingoFlashIframe: Fetching quizlet stats for session:', sessionId, 'participant:', participantId);
      const res = await fetch(`${baseUrl}/api/live-quiz/sessions/${sessionId}/participants/${participantId}/quizlet-stats`);
      if (!res.ok) {
        console.warn('BingoFlashIframe: Failed to fetch quizlet stats, status:', res.status);
        return;
      }
      const data = await res.json();
      // console.log('BingoFlashIframe: Received quizlet stats:', data);
      if (data.success && Array.isArray(data.stats)) {
        const statsMap = {};
        data.stats.forEach(stat => {
          statsMap[stat.quizlet_id] = stat;
        });
        // console.log('BingoFlashIframe: Stats map:', statsMap);
        setQuizletStats(statsMap);
        // Update the ref as well for immediate access
        quizletStatsRef.current = statsMap;
      }
    } catch (err) {
      console.error('BingoFlashIframe quizlet stats error:', err);
    }
  }, [baseUrl]);

  const fetchPushedQuizlets = useCallback(async (sessionId) => {
    try {
      const res = await fetch(`${baseUrl}/api/live-quiz/flash/sessions/${sessionId}/pushed-quizlets`);
      if (!res.ok) return;
      const data = await res.json();
      if (data.success && Array.isArray(data.items)) {
        // newest first from API; ensure running state only for the latest item
        const parsedRapid = (() => {
          const raw = session?.rapid_config;
          try { return typeof raw === 'string' ? JSON.parse(raw) : (raw || {}); } catch { return {}; }
        })();
        const songTimer = typeof parsedRapid.song_timer_seconds === 'number' ? parsedRapid.song_timer_seconds : 60;
        const items = data.items.map((it, idx) => {
          const deliveryMs = it.delivery_time ? new Date(it.delivery_time).getTime() : null;
          const expireAt = deliveryMs ? (deliveryMs + songTimer * 1000) : null;
          const now = Date.now();
          const isExpired = expireAt ? now > expireAt : false;
          const quizletId = it.id || `${it.song?.songs_master_id || 'song'}_${it.pushed_at}`;
          const userHasCompleted = quizletStatsRef.current[quizletId] !== undefined;
          return {
            id: quizletId,
            song: it.song || {},
            pushed_at: it.pushed_at,
            delivery_time: it.delivery_time,
            expire_at: expireAt,
            status: userHasCompleted ? 'completed' : (idx === 0 && !isExpired ? 'running' : 'expired'),
            userPlayed: userHasCompleted
          };
        });
        const runningItem = items.find(it => it.status === 'running') || items[0];
        activeQuizletIdRef.current = runningItem?.id || null;
        // Merge with existing to preserve userPlayed flags
        setPushedQuizlets(prev => {
          const prevById = new Map(prev.map(p => [p.id, p]));
          return items.map(item => {
            const existing = prevById.get(item.id);
            const userHasCompleted = quizletStatsRef.current[item.id] !== undefined;
            return existing ? { ...item, userPlayed: userHasCompleted || !!existing.userPlayed } : item;
          });
        });
      }
    } catch (e) {
      console.error('BingoFlashIframe: Failed to fetch pushed quizlets:', e);
    }
  }, [baseUrl, session?.rapid_config]);

  const fetchLeaderboard = useCallback(async (sessionId) => {
    if (!sessionId) return;
    try {
      const res = await fetch(`${baseUrl}/api/live-quiz/sessions/${sessionId}/leaderboard`);
      if (!res.ok) {
        throw new Error('Failed to fetch leaderboard');
      }
      const data = await res.json();
      setLeaderboard(Array.isArray(data.leaderboard) ? data.leaderboard : []);
    } catch (err) {
      console.error('BingoFlashIframe leaderboard fetch error:', err);
    }
  }, [baseUrl]);

  const joinSession = useCallback(async (sessionId) => {
    // Include guest_name when no auth token but guest_user exists
    let guestName = null;
    const token = localStorage.getItem('auth_token');
    if (!token) {
      try {
        const guestRaw = localStorage.getItem('guest_user');
        const guest = guestRaw ? JSON.parse(guestRaw) : null;
        if (guest && guest.user_type === 'guest') {
          guestName = getGuestDisplayName(guest);
        }
      } catch (_) {}
    }

    const payload = {
      device_id: getDeviceId(),
      anonymous_id: `anon_${Date.now()}`,
      guest_name: guestName,
    };
    const res = await fetch(`${baseUrl}/api/live-quiz/sessions/${sessionId}/join`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('auth_token') || ''}` },
      body: JSON.stringify(payload),
    });
    if (!res.ok) throw new Error('Failed to join session');
    return res.json();
  }, [baseUrl]);

  const clearIncomingCountdown = useCallback(() => {
    if (incomingCountdownIntervalRef.current) {
      clearInterval(incomingCountdownIntervalRef.current);
      incomingCountdownIntervalRef.current = null;
    }
    setIncomingCountdown(null);
  }, []);

  const startIncomingCountdown = useCallback((targetTime) => {
    if (!targetTime) return;
    clearIncomingCountdown();
    const tick = () => {
      const remaining = Math.ceil((targetTime - Date.now()) / 1000);
      if (remaining <= 3 && remaining > 0) {
        setIncomingCountdown(remaining);
      } else {
        setIncomingCountdown(null);
      }
      if (remaining <= 0) {
        clearIncomingCountdown();
      }
    };
    tick();
    incomingCountdownIntervalRef.current = setInterval(tick, 200);
  }, [clearIncomingCountdown]);

  // Immediate join on mount - check if user is returning to same session
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const active = await fetchActiveFlashSession();
        if (cancelled) return;
        setSession(active);
        
        // Check if user was previously in this same session
        const savedSessionId = localStorage.getItem('flash_session_id');
        const savedParticipantId = localStorage.getItem('flash_participant_id');
        
        // console.log('BingoFlashIframe: Checking saved session:', {
        //   activeSessionId: active.session_id,
        //   savedSessionId,
        //   savedParticipantId,
        //   isReturningUser: savedSessionId === String(active.session_id) && savedParticipantId
        // });
        
        let participantToUse;
        let isReturningUser = false;
        
        // If returning to same session, reuse participant
        if (savedSessionId === String(active.session_id) && savedParticipantId) {
          // console.log('BingoFlashIframe: Returning user - reusing participant:', savedParticipantId);
          participantToUse = {
            participant_id: parseInt(savedParticipantId, 10),
            session_id: active.session_id
          };
          isReturningUser = true;
        } else {
          // New session or first join - create new participant
          // console.log('BingoFlashIframe: New user - joining session');
          const joined = await joinSession(active.session_id);
          if (cancelled) return;
          localStorage.setItem('flash_session_id', String(active.session_id));
          localStorage.setItem('flash_participant_id', String(joined.participant_id));
          participantToUse = joined;
        }
        
        setParticipant(participantToUse);
        
        // Fetch quizlet stats for this participant
        await fetchQuizletStats(active.session_id, participantToUse.participant_id);
        
        // If returning user, also fetch pushed quizlets to restore state
        if (isReturningUser) {
          // console.log('BingoFlashIframe: Fetching pushed quizlets for returning user');
          await fetchPushedQuizlets(active.session_id);
        }
        
        // Leaderboard will be received via websocket after join
      } catch (e) {
        console.error('BingoFlashIframe init error:', e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [fetchActiveFlashSession, joinSession, fetchQuizletStats, fetchPushedQuizlets]);

  useEffect(() => {
    quizletStatsRef.current = quizletStats;
  }, [quizletStats]);

  // Socket listeners for flash events
  useEffect(() => {
    if (!session?.session_id) return;
    const socket = io(baseUrl, { transports: ['websocket'] });
    socketRef.current = socket;

    socket.on('connect', () => {
      socket.emit('flash:join', { sessionId: session.session_id });
      
      fetchLeaderboard(session.session_id);
      fetchPushedQuizlets(session.session_id);
      if (participant?.participant_id) {
        fetchQuizletStats(session.session_id, participant.participant_id);
      }
    });
    const unbindResync = bindGameResync(() => {
      if (socket.connected) socket.emit('flash:join', { sessionId: session.session_id });
      fetchLeaderboard(session.session_id);
      fetchPushedQuizlets(session.session_id);
      if (participant?.participant_id) {
        fetchQuizletStats(session.session_id, participant.participant_id);
      }
    });

    socket.on('flash:question_pushed', (payload) => {
      if (!payload || payload.session_id !== session.session_id) return;
      // Handle delay_seconds
      try { if (deliveryTimerRef.current) { clearTimeout(deliveryTimerRef.current); deliveryTimerRef.current = null; } } catch (_) {}
      clearIncomingCountdown();
      const deliveryTime = payload.delivery_time ? new Date(payload.delivery_time).getTime() : Date.now();
      const now = Date.now();
      const delayMs = Math.max(0, deliveryTime - now);

      // Cache current payload so user can play Running quiz again on click
      setCurrentFlashPayload({ current_song: payload.current_song, questions: payload.questions, delivery_time: payload.delivery_time });

      // Update pushed quizlets (newest first)
      const newId = `${payload.current_song?.songs_master_id || 'song'}_${payload.pushed_at || Date.now()}`;
      activeQuizletIdRef.current = newId;
      setPushedQuizlets(prev => {
        const parsedRapid = (() => {
          const raw = session?.rapid_config;
          try { return typeof raw === 'string' ? JSON.parse(raw) : (raw || {}); } catch { return {}; }
        })();
        const songTimer = typeof parsedRapid.song_timer_seconds === 'number' ? parsedRapid.song_timer_seconds : 60;
        const updated = prev.map((q, idx) => (idx === 0 && q.status === 'running') ? { ...q, status: 'expired' } : q);
        const userHasCompleted = quizletStatsRef.current[newId] !== undefined;
        const newItem = {
          id: newId,
          song: payload.current_song || {},
          pushed_at: payload.pushed_at,
          delivery_time: payload.delivery_time,
          expire_at: payload.delivery_time ? (new Date(payload.delivery_time).getTime() + songTimer * 1000) : null,
          status: userHasCompleted ? 'completed' : 'running',
          userPlayed: userHasCompleted
        };
        return [newItem, ...updated];
      });
      if (participant?.participant_id) {
        setTimeout(() => {
          fetchQuizletStats(session.session_id, participant.participant_id);
        }, 1000);
      }
      const showQuestion = () => {
        // compute remaining seconds from delivery_time + configured timer
        const parsedRapid = (() => {
          const raw = session?.rapid_config;
          try { return typeof raw === 'string' ? JSON.parse(raw) : (raw || {}); } catch { return {}; }
        })();
        const baseTimer = typeof parsedRapid.song_timer_seconds === 'number' ? parsedRapid.song_timer_seconds : 60;
        const expireAt = payload.delivery_time ? (new Date(payload.delivery_time).getTime() + baseTimer * 1000) : null;
        const nowTs = Date.now();
        const remain = expireAt ? Math.max(0, Math.floor((expireAt - nowTs) / 1000)) : baseTimer;
        setRemainingSecondsForCurrent(remain);
        // baseTimer available for UI if needed
        const formatted = {
          quiz_data: [{ song: payload.current_song, questions: payload.questions }],
          difficulty: { base_time: 30 },
        };
        setQuizData(formatted);
        setQuestionVersion(v => v + 1);
        setCurrentPlayingId(newId);
        setShowQuestion(true);
      };
      if (delayMs > 0) {
        deliveryTimerRef.current = setTimeout(showQuestion, delayMs);
        startIncomingCountdown(deliveryTime);
      } else {
        showQuestion();
      }
    });

    socket.on('flash:leaderboard', (payload) => {
      if (payload && payload.session_id === session.session_id) {
        setLeaderboard(payload.leaderboard || []);
      }
    });

    socket.on('flash:quizlet_rank_updated', (payload) => {
      if (!payload || Number(payload.session_id) !== Number(session.session_id)) return;
      if (participant?.participant_id) {
        fetchQuizletStats(session.session_id, participant.participant_id);
      }
    });

    socket.on('flash:session_ended', (payload) => {
      if (!payload || payload.session_id !== session.session_id) return;
      // End flow: show leaderboard popup, hide question overlay
      setShowLb(true);
      setShowQuestion(false);
    });

    return () => {
      unbindResync();
      try {
        socket.emit('flash:leave', { sessionId: session.session_id });
        socket.disconnect();
      } catch (_) {}
      try { if (deliveryTimerRef.current) { clearTimeout(deliveryTimerRef.current); deliveryTimerRef.current = null; } } catch (_) {}
      clearIncomingCountdown();
    };
  }, [session?.session_id, session?.rapid_config, baseUrl, startIncomingCountdown, clearIncomingCountdown, participant?.participant_id, fetchQuizletStats, fetchLeaderboard, fetchPushedQuizlets]);

  useEffect(() => {
    return () => {
      clearIncomingCountdown();
      try { if (deliveryTimerRef.current) { clearTimeout(deliveryTimerRef.current); deliveryTimerRef.current = null; } } catch (_) {}
    };
  }, [clearIncomingCountdown]);

  useEffect(() => {
    if (session?.session_id) {
      fetchLeaderboard(session.session_id);
    }
  }, [session?.session_id, fetchLeaderboard]);

  useEffect(() => {
    if (session?.session_id && participant?.participant_id) {
      fetchQuizletStats(session.session_id, participant.participant_id);
    }
  }, [session?.session_id, participant?.participant_id, fetchQuizletStats]);

  const submitQuizletResults = useCallback(async (results, quizletId, songData) => {
    try {
      if (!session?.session_id) {
        console.error('BingoFlashIframe: No session_id available');
        return false;
      }
      
      const participantId = participant?.participant_id || localStorage.getItem('flash_participant_id');
      if (!participantId) {
        console.error('BingoFlashIframe: No participant_id available');
        return false;
      }
      
      const songId = songData?.songs_master_id || songData?.song?.songs_master_id || null;
      const questionsCount = results?.totalQuestions || results?.questionResults?.length || 0;
      
      // Get auth token - prefer auth_token, fallback to user_token
      const authToken = localStorage.getItem('auth_token') || localStorage.getItem('user_token') || '';
      
      const payload = {
        participant_id: parseInt(participantId, 10),
        quizlet_results: results,
        total_score: results?.totalScore || 0,
        quizlet_id: quizletId,
        song_id: songId,
        questions_count: questionsCount
      };
      
      // console.log('BingoFlashIframe: Submitting quizlet results:', {
      //   sessionId: session.session_id,
      //   participantId: payload.participant_id,
      //   quizletId,
      //   songId,
      //   totalScore: payload.total_score,
      //   questionsCount,
      //   hasToken: !!authToken
      // });
      
      const res = await fetch(`${baseUrl}/api/live-quiz/sessions/${session.session_id}/answer`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify(payload)
      });
      
      if (!res.ok) {
        const errorText = await res.text();
        console.error('BingoFlashIframe: Submit failed:', res.status, errorText);
        return false;
      }
      
      await res.json();
      // console.log('BingoFlashIframe: Submit successful:', responseData);
      return true;
    } catch (e) {
      console.error('BingoFlashIframe: submit results error:', e);
      return false;
    }
  }, [baseUrl, session?.session_id, participant?.participant_id]);

  const handleQuestionComplete = useCallback(async (results) => {
    const completedId = currentPlayingId || activeQuizletIdRef.current;

    // console.log('BingoFlashIframe: Question complete:', {
    //   completedId,
    //   hasResults: !!results,
    //   totalScore: results?.totalScore,
    //   questionsCount: results?.totalQuestions
    // });

    if (results && completedId) {
      // Find the song data for this quizlet
      const currentQuizlet = pushedQuizlets.find(q => q.id === completedId);
      const songData = currentQuizlet?.song || quizData?.quiz_data?.[0]?.song || null;
      
      // console.log('BingoFlashIframe: Submitting results for quizlet:', completedId);
      const submitted = await submitQuizletResults(results, completedId, songData);
      
      if (submitted) {
        // console.log('BingoFlashIframe: Results submitted successfully');
      } else {
        console.error('BingoFlashIframe: Failed to submit results');
      }
    }

    setShowQuestion(false);
    setIncomingCountdown(null);
    setPushedQuizlets(prev =>
      prev.map((q, idx) => {
        const shouldMark = completedId ? q.id === completedId : idx === 0;
        return shouldMark ? { ...q, userPlayed: true, status: 'completed' } : q;
      })
    );
    setCurrentPlayingId(null);

    // Refresh leaderboard and stats after submission
    if (session?.session_id && participant?.participant_id) {
      setTimeout(() => {
        // console.log('BingoFlashIframe: Refreshing leaderboard and stats');
        fetchLeaderboard(session.session_id);
        fetchQuizletStats(session.session_id, participant.participant_id);
      }, 1500);
    }
  }, [currentPlayingId, session?.session_id, participant?.participant_id, fetchQuizletStats, fetchLeaderboard, submitQuizletResults, pushedQuizlets, quizData]);

  // Fetch pushed quizlets & leaderboard when dialog opens
  useEffect(() => {
    if (showLb && session?.session_id) {
      fetchPushedQuizlets(session.session_id);
      fetchLeaderboard(session.session_id);
      if (participant?.participant_id) {
        fetchQuizletStats(session.session_id, participant.participant_id);
      }
    }
  }, [showLb, session?.session_id, fetchPushedQuizlets, fetchLeaderboard, participant?.participant_id, fetchQuizletStats]);

  // If quiz is active, ensure leaderboard dialog is closed/blocked
  useEffect(() => {
    if (showQuestion && showLb) setShowLb(false);
  }, [showQuestion, showLb]);

  const handlePlayRunning = useCallback(() => {
    // Only allow playing if there is a running item and user hasn't played it
    const top = pushedQuizlets[0];
    if (!top || top.status !== 'running' || top.userPlayed) return;
    const payload = currentFlashPayload;
    const proceed = (pl) => {
      const now = Date.now();
      const expireAt = top.expire_at || (pl.delivery_time ? (new Date(pl.delivery_time).getTime() + 60000) : null);
      const remain = expireAt ? Math.max(0, Math.floor((expireAt - now) / 1000)) : null;
      setRemainingSecondsForCurrent(remain);
      // Base timer is handled by flashConfig passed to QuizQuestion
      const formatted = {
        quiz_data: [{ song: pl.current_song, questions: pl.questions }],
        difficulty: { base_time: 30 }
      };
      setQuizData(formatted);
      setQuestionVersion(v => v + 1);
      setCurrentPlayingId(top.id);
      setShowQuestion(true);
    };

    if (payload && payload.current_song && Array.isArray(payload.questions)) {
      proceed(payload);
    } else {
      // Late joiner fallback: fetch current questions for flash session
      (async () => {
        try {
          const res = await fetch(`${baseUrl}/api/live-quiz/flash/sessions/${session.session_id}/current-questions`);
          if (!res.ok) return;
          const data = await res.json();
          if (data.success && data.has_active_questions) {
            proceed({ current_song: data.current_song, questions: data.questions, delivery_time: data.delivery_time });
          }
        } catch (_) {}
      })();
    }
  }, [pushedQuizlets, currentFlashPayload, baseUrl, session?.session_id]);

  // Periodically set items to expired when time elapses
  useEffect(() => {
    const timer = setInterval(() => {
      setPushedQuizlets(prev => prev.map((q, idx) => {
        if (q.status === 'expired') return q;
        const now = Date.now();
        if (q.expire_at && now >= q.expire_at) {
          return { ...q, status: 'expired' };
        }
        if (idx > 0 && q.status === 'running') {
          return { ...q, status: 'expired' };
        }
        return q;
      }));
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // Local overlay countdown: close and expire when time runs out
  useEffect(() => {
    if (!showQuestion || typeof remainingSecondsForCurrent !== 'number') return;
    if (remainingSecondsForCurrent <= 0) {
      setShowQuestion(false);
      // if not completed, mark current running item as expired locally
      setPushedQuizlets(prev => prev.map(q => (q.id === currentPlayingId && !q.userPlayed) ? { ...q, status: 'expired' } : q));
      setCurrentPlayingId(null);
      // reset base timer UI if used in future
      return;
    }
    const iv = setInterval(() => {
      setRemainingSecondsForCurrent(s => (typeof s === 'number' ? Math.max(0, s - 1) : s));
    }, 1000);
    return () => clearInterval(iv);
  }, [showQuestion, remainingSecondsForCurrent, currentPlayingId]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#fff' }}>
        <CircularProgress sx={{ color: '#9C27B0' }} />
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', height: '100%', position: 'relative', bgcolor: '#000' }}>
      {/* Leaderboard toggle */}
      <Box sx={{ position: 'absolute', top: 4, left: 4, right: 56, zIndex: 2, display: 'flex', alignItems: 'center', gap: 1, overflow: 'hidden' }}>
        <Tooltip title={showQuestion ? 'Quiz in progress' : 'Show Leaderboard'}>
          <IconButton onClick={() => { if (!showQuestion) setShowLb(true); }} disabled={!!showQuestion} sx={{ ml: 'auto', color: '#fff', backgroundColor: 'rgba(255,255,255,0.1)' }}>
            <LeaderboardIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Iframe Bingo game */}
      <Box sx={{ position: 'absolute', inset: 0 }}>
        <iframe
          src={url}
          title="Bingo"
          width="100%"
          height="100%"
          frameBorder="0"
          allowFullScreen
          style={{ border: 'none', backgroundColor: '#000' }}
        />
      </Box>

      {/* Incoming countdown overlay */}
      {incomingCountdown !== null && !showQuestion && (
        <IncomingCountdownOverlay value={incomingCountdown} />
      )}

      {/* Question overlay */}
      {showQuestion && quizData && (
        <Box sx={{ 
          position: 'absolute', 
          inset: 0, 
          backgroundColor: 'rgba(0,0,0,0.95)', 
          display: 'flex', 
          alignItems: 'flex-start', 
          justifyContent: 'center', 
          zIndex: 3,
          overflowY: 'auto',
          overflowX: 'hidden',
          py: 2
        }}>
          <Box sx={{ 
            width: '100%', 
            maxWidth: '900px', 
            px: 2,
            py: 2
          }}>
            {/* Top progress line */}
            {/* {typeof remainingSecondsForCurrent === 'number' && typeof totalSecondsForCurrent === 'number' && totalSecondsForCurrent > 0 && (
              <Box sx={{ px: 0, mb: 1.5 }}>
                <Box sx={{ height: 6, bgcolor: 'rgba(255,255,255,0.08)', borderRadius: 9999, overflow: 'hidden', border: '1px solid #1f1f1f' }}>
                  <Box
                    sx={{
                      height: '100%',
                      width: `${Math.min(100, Math.max(0, ((totalSecondsForCurrent - remainingSecondsForCurrent) / totalSecondsForCurrent) * 100))}%`,
                      transition: 'width 1000ms linear',
                      background: 'linear-gradient(90deg, #9C27B0, #E1BEE7)',
                    }}
                  />
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                  <Typography variant="caption" sx={{ color: '#bdbdbd' }}>Time Remaining</Typography>
                  <Typography variant="caption" sx={{ color: '#e0e0e0', fontFamily: 'monospace' }}>{remainingSecondsForCurrent}s</Typography>
                </Box>
              </Box>
            )} */}
            <QuizQuestion
              key={questionVersion}
              config={{ difficulty_id: session?.quiz_config?.difficulty_id || 7, duration_minutes: 5, questions_per_song: session?.quiz_config?.questions_per_song || 3 }}
              quizData={quizData}
              currentQuizletIndex={0}
              onComplete={async (_quizletIndex, results) => {
                await handleQuestionComplete(results);
              }}
              mode="flash"
              flashConfig={(() => {
                const raw = session?.rapid_config;
                const parsed = typeof raw === 'string' ? (() => { try { return JSON.parse(raw); } catch { return {}; } })() : (raw || {});
                const baseTimer = typeof parsed.song_timer_seconds === 'number' ? parsed.song_timer_seconds : 60;
                return {
                  delay_seconds: typeof parsed.delay_seconds === 'number' ? parsed.delay_seconds : 10,
                  song_timer_seconds: typeof remainingSecondsForCurrent === 'number' ? remainingSecondsForCurrent : baseTimer,
                  base_song_timer_seconds: baseTimer
                };
              })()}
            />
          </Box>
        </Box>
      )}

      {/* Leaderboard dialog */}
      <Dialog
        open={showLb}
        onClose={() => setShowLb(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { bgcolor: '#111', color: '#fff', border: '1px solid #333' } }}
      >
        {/* <DialogTitle>Leaderboard</DialogTitle> */}
        <DialogContent>
          {/* Leaderboard */}
          <Box sx={{ mb: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Typography
                variant="subtitle1"
                sx={{ color: '#e5e7eb', fontWeight: 600 }}
              >
                Live Leaderboard
              </Typography>
              <Chip
                label={`${leaderboard?.length || 0} players`}
                sx={{ backgroundColor: '#1f2937', color: '#e5e7eb', borderRadius: 1 }}
              />
            </Box>
            {leaderboard?.length ? (
              (() => {
                const topEntries = leaderboard.slice(0, 10);
                return (
                  <List sx={{ bgcolor: '#020617', borderRadius: 2, border: '1px solid rgba(148, 163, 184, 0.25)' }}>
                    {topEntries.map((entry, index) => {
                      const isCurrentUser = entry.participant_id === participant?.participant_id;
                      const position = index + 1;
                      const displayName = (() => {
                        const guest = (entry.guest_name || '').trim();
                        if (guest) return guest;
                        const fname = (entry.fname || '').trim();
                        const lname = (entry.lname || '').trim();
                        const combined = `${fname} ${lname}`.trim();
                        return combined || (entry.anonymous_id || 'Anonymous');
                      })();
                      return (
                        <ListItem
                          key={entry.participant_id || index}
                          sx={{
                            py: 2,
                            borderBottom: index === topEntries.length - 1 ? 'none' : '1px solid rgba(31,41,55,0.7)',
                            backgroundColor: isCurrentUser ? 'rgba(168, 85, 247, 0.08)' : 'transparent',
                            borderRadius: '8px',
                            mb: 1,
                            border: isCurrentUser ? '1px solid rgba(168, 85, 247, 0.7)' : '1px solid transparent'
                          }}
                        >
                          <ListItemAvatar>
                            <Avatar
                              sx={{
                                bgcolor:
                                  position === 1
                                    ? '#facc15'
                                    : position === 2
                                      ? '#e5e7eb'
                                      : position === 3
                                        ? '#fb7185'
                                        : '#5684f0',
                                width: 40,
                                height: 40,
                                fontSize: '1rem',
                                fontWeight: 'bold'
                              }}
                            >
                              {position}
                            </Avatar>
                          </ListItemAvatar>
                          <ListItemText
                            primary={
                              <Typography variant="body1" sx={{ color: '#e5e7eb', fontWeight: 500 }}>
                                {displayName}
                                {isCurrentUser && (
                                  <Chip
                                    label="You"
                                    size="small"
                                    sx={{
                                      ml: 1,
                                      backgroundColor: '#a855f7',
                                      color: '#020617',
                                      fontSize: '0.7rem'
                                    }}
                                  />
                                )}
                              </Typography>
                            }
                            secondary={
                              <Typography variant="body2" sx={{ color: '#9ca3af' }}>
                                {entry.current_score || 0} points
                              </Typography>
                            }
                          />
                        </ListItem>
                      );
                    })}
                    {leaderboard.length > 10 && (
                      <ListItem sx={{ py: 2 }}>
                        <Typography variant="body2" sx={{ color: '#666', textAlign: 'center', width: '100%' }}>
                          ... and {leaderboard.length - 10} more players
                        </Typography>
                      </ListItem>
                    )}
                  </List>
                );
              })()
            ) : (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="body1" sx={{ color: '#6b7280' }}>
                  No players on leaderboard yet
                </Typography>
              </Box>
            )}
          </Box>

          {/* Quizlet Status */}
          <Box sx={{ mt: 4, pt: 2, borderTop: '1px solid #222' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="subtitle1" sx={{ color: '#e5e7eb', fontWeight: 600 }}>
                Quizlet status
              </Typography>
              <Chip
                label={`${pushedQuizlets.length} quizlets`}
                sx={{ backgroundColor: '#1f2937', color: '#e5e7eb', borderRadius: 1 }}
              />
            </Box>
            {pushedQuizlets?.length ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {pushedQuizlets.map((q, idx) => {
                  const title = q.song?.name || q.song?.title || 'Unknown Song';
                  const artist =
                    q.song?.artist ||
                    (Array.isArray(q.song?.artists) ? q.song?.artists.join(', ') : q.song?.artist_name) ||
                    'Unknown Artist';
                  const image = q.song?.image || q.song?.cover || q.song?.thumbnail || q.song?.artwork;
                  const stats = quizletStats[q.id] || null;
                  const hasStats = !!stats;
                  const timeTakenSeconds = stats ? getQuizletTimeTakenSeconds(stats) : null;
                  const isCompleted = q.status === 'completed' || hasStats || q.userPlayed;
                  const isRunning = q.status === 'running' && idx === 0 && !isCompleted;
                  
                  // Debug logging for first quizlet
                  if (idx === 0) {
                    // console.log('BingoFlashIframe: Rendering quizlet:', {
                      // quizletId: q.id,
                      // hasStats,
                      // stats,
                      // isCompleted,
                      // userPlayed: q.userPlayed,
                      // status: q.status,
                      // allStatsKeys: Object.keys(quizletStats)
                    // });
                  }

                  return (
                    <Box
                      key={q.id || idx}
                      sx={{
                        borderRadius: 2,
                        border: '1px solid rgba(31, 41, 55, 0.9)',
                        background: isCompleted
                          ? 'linear-gradient(90deg, rgba(34,197,94,0.10), rgba(15,23,42,0.9))'
                          : isRunning
                            ? 'linear-gradient(90deg, rgba(129,140,248,0.12), rgba(15,23,42,0.9))'
                            : '#020617',
                        overflow: 'hidden'
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1.5, px: 2 }}>
                        <Box
                          sx={{
                            width: 56,
                            height: 56,
                            borderRadius: 2,
                            bgcolor: '#020617',
                            border: '1px solid rgba(31,41,55,0.9)',
                            overflow: 'hidden',
                            flexShrink: 0
                          }}
                        >
                          {image ? (
                            <img src={image} alt={title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                          ) : (
                            <Box
                              sx={{
                                width: '100%',
                                height: '100%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: '#777',
                                fontSize: 12
                              }}
                            >
                              No Artist
                            </Box>
                          )}
                        </Box>

                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography
                            variant="body1"
                            sx={{
                              color: '#e5e7eb',
                              fontWeight: 500,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            {title}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              color: '#9ca3af',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            {artist}
                          </Typography>
                        </Box>

                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                          {isCompleted ? (
                            <Chip
                              size="small"
                              label="Completed"
                              sx={{
                                bgcolor: 'rgba(34,197,94,0.12)',
                                color: '#bbf7d0',
                                border: '1px solid rgba(22,163,74,0.5)'
                              }}
                            />
                          ) : isRunning ? (
                            <Chip
                              size="small"
                              label="Running"
                              onClick={handlePlayRunning}
                              sx={{
                                cursor: 'pointer',
                                bgcolor: 'rgba(129,140,248,0.15)',
                                color: '#e0e7ff',
                                border: '1px solid rgba(79,70,229,0.6)'
                              }}
                            />
                          ) : (
                            <Chip
                              size="small"
                              label="Expired"
                              sx={{
                                bgcolor: 'rgba(31,41,55,0.8)',
                                color: '#e5e7eb',
                                border: '1px solid rgba(55,65,81,0.9)'
                              }}
                            />
                          )}
                        </Box>
                      </Box>

                      {hasStats && (
                        <Box sx={{ px: 2, pb: 2 }}>
                          <Divider sx={{ mb: 2, borderColor: '#111827' }} />
                          <Grid container spacing={2}>
                            <Grid item xs={6} sm={3}>
                              <Box
                                sx={{
                                  p: 2,
                                  borderRadius: 2,
                                  bgcolor: 'rgba(59,130,246,0.10)',
                                  border: '1px solid rgba(59,130,246,0.35)',
                                  textAlign: 'center'
                                }}
                              >
                                <Typography variant="h5" sx={{ color: '#60a5fa', fontWeight: 600, mb: 0.5 }}>
                                  {stats.questions_count || 0}
                                </Typography>
                                <Typography variant="body2" sx={{ color: '#9ca3af', fontSize: '0.75rem' }}>
                                  Questions
                                </Typography>
                              </Box>
                            </Grid>
                            <Grid item xs={6} sm={3}>
                              <Box
                                sx={{
                                  p: 2,
                                  borderRadius: 2,
                                  bgcolor: 'rgba(34,197,94,0.10)',
                                  border: '1px solid rgba(34,197,94,0.35)',
                                  textAlign: 'center'
                                }}
                              >
                                <Typography variant="h5" sx={{ color: '#4ade80', fontWeight: 600, mb: 0.5 }}>
                                  {stats.total_points || 0}
                                </Typography>
                                <Typography variant="body2" sx={{ color: '#9ca3af', fontSize: '0.75rem' }}>
                                  Total Points
                                </Typography>
                              </Box>
                            </Grid>
                            <Grid item xs={6} sm={3}>
                              <Box
                                sx={{
                                  p: 2,
                                  borderRadius: 2,
                                  bgcolor: 'rgba(250, 204, 21, 0.10)',
                                  border: '1px solid rgba(250, 204, 21, 0.35)',
                                  textAlign: 'center'
                                }}
                              >
                                <Typography variant="h5" sx={{ color: '#fde047', fontWeight: 600, mb: 0.5 }}>
                                  {stats.quizlet_rank ? (
                                    `#${stats.quizlet_rank}`
                                  ) : (
                                    <CircularProgress size={24} sx={{ color: '#fde047', mt: 0.5 }} />
                                  )}
                                </Typography>
                                <Typography variant="body2" sx={{ color: '#9ca3af', fontSize: '0.75rem' }}>
                                  Quizlet Rank
                                </Typography>
                              </Box>
                            </Grid>
                            <Grid item xs={6} sm={3}>
                              <Box
                                sx={{
                                  p: 2,
                                  borderRadius: 2,
                                  bgcolor: 'rgba(129, 140, 248, 0.10)',
                                  border: '1px solid rgba(129, 140, 248, 0.35)',
                                  textAlign: 'center'
                                }}
                              >
                                <Typography variant="h5" sx={{ color: '#a855f7', fontWeight: 600, mb: 0.5 }}>
                                  {typeof timeTakenSeconds === 'number' ? `${timeTakenSeconds}s` : 'N/A'}
                                </Typography>
                                <Typography variant="body2" sx={{ color: '#9ca3af', fontSize: '0.75rem' }}>
                                  Time Taken
                                </Typography>
                              </Box>
                            </Grid>
                          </Grid>
                        </Box>
                      )}
                    </Box>
                  );
                })}
              </Box>
            ) : (
              <Box sx={{ py: 1 }}>
                <Typography variant="body2" sx={{ color: '#9aa0a6' }}>
                  No quizlets pushed yet
                </Typography>
              </Box>
            )}
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default BingoFlashIframe;


